import { useState, useEffect, useCallback } from 'react';
import { api, type CollectionLog } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Play, Loader2, CheckCircle2, XCircle, Clock, Zap } from 'lucide-react';

function formatTime(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

function formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}

function formatTimeAgo(timestamp: number): string {
    const seconds = Math.floor(Date.now() / 1000 - timestamp);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
}

export function Collector() {
    const [logs, setLogs] = useState<CollectionLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [collecting, setCollecting] = useState(false);
    const [lastCollectResult, setLastCollectResult] = useState<CollectionLog | null>(null);

    // Stats
    const totalFetched = logs.reduce((sum, l) => sum + l.tradesFetched, 0);
    const totalProcessed = logs.reduce((sum, l) => sum + l.tradesProcessed, 0);
    const totalAlerts = logs.reduce((sum, l) => sum + l.alertsCreated, 0);
    const successRate = logs.length > 0
        ? Math.round((logs.filter(l => l.status === 'success').length / logs.length) * 100)
        : 0;

    const fetchLogs = useCallback(async () => {
        try {
            const res = await api.get<{ success: boolean; data: CollectionLog[] }>(
                '/api/internal/logs?limit=50'
            );
            if (res.success) {
                setLogs(res.data);
            }
        } catch (err) {
            console.error('Failed to fetch logs:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs();
        // Refresh every 30 seconds
        const interval = setInterval(fetchLogs, 30000);
        return () => clearInterval(interval);
    }, [fetchLogs]);

    async function handleCollect() {
        setCollecting(true);
        setLastCollectResult(null);

        try {
            const res = await api.post<{ success: boolean; data: CollectionLog }>(
                '/api/internal/collect',
                {}
            );
            if (res.success) {
                setLastCollectResult(res.data);
                await fetchLogs();
            }
        } catch (err) {
            console.error('Collection failed:', err);
        } finally {
            setCollecting(false);
        }
    }

    const StatusIcon = ({ status }: { status: string }) => {
        if (status === 'success') return <CheckCircle2 className="w-4 h-4 text-green-400" />;
        if (status === 'error') return <XCircle className="w-4 h-4 text-red-400" />;
        return <Clock className="w-4 h-4 text-yellow-400 animate-spin" />;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Zap className="w-6 h-6 text-primary" />
                        Collector
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Polymarket trade collection and processing
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchLogs}
                        disabled={loading}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                        onClick={handleCollect}
                        disabled={collecting}
                        className="gap-2"
                    >
                        {collecting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Collecting...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4" />
                                Collect Now
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                            Trades Fetched
                        </div>
                        <div className="text-2xl font-bold font-mono">{totalFetched.toLocaleString()}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                            Trades Processed
                        </div>
                        <div className="text-2xl font-bold font-mono text-green-400">
                            {totalProcessed.toLocaleString()}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                            Alerts Created
                        </div>
                        <div className="text-2xl font-bold font-mono text-primary">
                            {totalAlerts}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                            Success Rate
                        </div>
                        <div className="text-2xl font-bold font-mono">
                            {successRate}%
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Last Result Toast */}
            {lastCollectResult && (
                <Card className="border-primary/30 bg-primary/5">
                    <CardContent className="py-4">
                        <div className="flex items-center gap-3">
                            <StatusIcon status={lastCollectResult.status} />
                            <div className="flex-1">
                                <span className="font-medium">Collection complete:</span>{' '}
                                <span className="text-muted-foreground">
                                    {lastCollectResult.tradesFetched} fetched →{' '}
                                    {lastCollectResult.tradesSignificant} significant →{' '}
                                    {lastCollectResult.tradesProcessed} processed
                                </span>
                            </div>
                            {lastCollectResult.alertsCreated > 0 && (
                                <Badge variant="default">
                                    🚨 {lastCollectResult.alertsCreated} new alerts
                                </Badge>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Activity Log */}
            <Card>
                <CardHeader>
                    <CardTitle>Collection History</CardTitle>
                    <CardDescription>
                        Recent collection runs (auto-refreshes every 30s)
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    {loading && logs.length === 0 ? (
                        <div className="flex items-center justify-center py-12 text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Loading logs...
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            No collection logs yet. Click "Collect Now" to start.
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {logs.map((log) => (
                                <div
                                    key={log.id}
                                    className="flex items-center gap-4 px-6 py-3 hover:bg-muted/50 transition-colors"
                                >
                                    <StatusIcon status={log.status} />
                                    <div className="w-20 shrink-0">
                                        <div className="font-mono text-sm">{formatTime(log.startedAt)}</div>
                                        <div className="text-xs text-muted-foreground">{formatDate(log.startedAt)}</div>
                                    </div>
                                    <div className="flex-1 flex items-center gap-3 min-w-0">
                                        {log.status === 'error' ? (
                                            <span className="text-red-400 truncate">
                                                {log.errorMessage || 'Collection failed'}
                                            </span>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-muted-foreground text-sm">Fetched</span>
                                                    <span className="font-mono text-sm">{log.tradesFetched}</span>
                                                </div>
                                                <span className="text-muted-foreground">→</span>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-muted-foreground text-sm">Significant</span>
                                                    <span className={`font-mono text-sm ${log.tradesSignificant > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                        {log.tradesSignificant}
                                                    </span>
                                                </div>
                                                <span className="text-muted-foreground">→</span>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-muted-foreground text-sm">Processed</span>
                                                    <span className={`font-mono text-sm ${log.tradesProcessed > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                                                        {log.tradesProcessed}
                                                    </span>
                                                </div>
                                                {log.alertsCreated > 0 && (
                                                    <Badge variant="default" className="text-xs ml-2">
                                                        🚨 {log.alertsCreated}
                                                    </Badge>
                                                )}
                                                {log.errors > 0 && (
                                                    <Badge variant="destructive" className="text-xs">
                                                        {log.errors} errors
                                                    </Badge>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <span className="text-muted-foreground text-xs shrink-0 w-16 text-right">
                                        {formatTimeAgo(log.finishedAt)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
